/**
 * Convert hex colors to OKLCH values
 * This helps us get the exact OKLCH values for our brand colors
 */

// Convert hex to RGB
function hexToRgb(hex: string): [number, number, number] {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
	if (!result) throw new Error('Invalid hex color')
	
	return [
		parseInt(result[1], 16) / 255,
		parseInt(result[2], 16) / 255,
		parseInt(result[3], 16) / 255
	]
}

// sRGB to linear RGB
function srgbToLinear(c: number): number {
	return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

// Linear RGB to XYZ
function rgbToXyz(r: number, g: number, b: number): [number, number, number] {
	r = srgbToLinear(r)
	g = srgbToLinear(g)
	b = srgbToLinear(b)
	
	const x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375
	const y = r * 0.2126729 + g * 0.7151522 + b * 0.0721750
	const z = r * 0.0193339 + g * 0.1191920 + b * 0.9503041
	
	return [x, y, z]
}

// XYZ to OKLab
function xyzToOklab(x: number, y: number, z: number): [number, number, number] {
	const l = 0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z
	const m = 0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z
	const s = 0.0482003018 * x + 0.2643662691 * y + 0.6338517070 * z
	
	const l_ = Math.cbrt(l)
	const m_ = Math.cbrt(m)
	const s_ = Math.cbrt(s)
	
	const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_
	const a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_
	const b = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_
	
	return [L, a, b]
}

// OKLab to OKLCH
function oklabToOklch(L: number, a: number, b: number): [number, number, number] {
	const C = Math.sqrt(a * a + b * b)
	let h = Math.atan2(b, a) * 180 / Math.PI
	if (h < 0) h += 360
	
	return [L, C, h]
}

export function hexToOklch(hex: string): string {
	const [r, g, b] = hexToRgb(hex)
	const [x, y, z] = rgbToXyz(r, g, b)
	const [l, a, b_] = xyzToOklab(x, y, z)
	const [L, C, h] = oklabToOklch(l, a, b_)
	
	// Round to reasonable precision
	const L_rounded = Math.round(L * 10000) / 10000
	const C_rounded = Math.round(C * 10000) / 10000
	const h_rounded = Math.round(h * 100) / 100
	
	return `oklch(${L_rounded} ${C_rounded} ${h_rounded})`
}

// Test our brand colors
export function testBrandColorConversions() {
	const brandColors = {
		primary: '#3A2E2F',
		primaryContrast: '#FFFDFD',
		primaryAction: '#621984',
		primaryLight: '#81479C',
		hexGrid: '#878DDD',
	}
	
	console.log('Brand Color OKLCH Conversions:')
	console.log('==============================')
	
	Object.entries(brandColors).forEach(([name, hex]) => {
		try {
			const oklch = hexToOklch(hex)
			console.log(`${name}: ${hex} => ${oklch}`)
		} catch (error) {
			console.error(`Error converting ${name}: ${error}`)
		}
	})
}